/**
 * Formulario de configuración de Cable
 * Permite configurar buffers, fibras y sus colores según estándar
 * @module components/forms/CableConfigForm
 */

import { useState, useEffect } from 'react'
import { Layers, Circle, Ruler } from 'lucide-react'
import type { EstadoExtremoFibra, EstadoFibra } from '@/types/fibers'
import { longitudRealCable } from '@/services/opticalBudgetService'

// Colores estándar de fibra (TIA-598)
const COLORES_FIBRA = [
  { nombre: 'Azul', codigo: 'azul', hex: '#0066CC' },
  { nombre: 'Naranja', codigo: 'naranja', hex: '#FF6600' },
  { nombre: 'Verde', codigo: 'verde', hex: '#00AA00' },
  { nombre: 'Marrón', codigo: 'marron', hex: '#8B4513' },
  { nombre: 'Gris', codigo: 'gris', hex: '#808080' },
  { nombre: 'Blanco', codigo: 'blanco', hex: '#FFFFFF' },
  { nombre: 'Rojo', codigo: 'rojo', hex: '#FF0000' },
  { nombre: 'Negro', codigo: 'negro', hex: '#000000' },
  { nombre: 'Amarillo', codigo: 'amarillo', hex: '#FFCC00' },
  { nombre: 'Violeta', codigo: 'violeta', hex: '#9900CC' },
  { nombre: 'Rosa', codigo: 'rosa', hex: '#FF66CC' },
  { nombre: 'Turquesa', codigo: 'turquesa', hex: '#00CCCC' },
]

// Estados que tiene sentido dejar corregir a mano desde aquí. 'en_paso'/'cliente'/'olt'
// los pone el propio flujo que corresponde (empalme, puerto NAP, sync de OLT).
const ESTADOS_MANUALES: EstadoFibra[] = ['libre', 'fusionado', 'derivado', 'muerto', 'reserva']
const ESTADO_ICONO: Record<EstadoFibra, string> = {
  libre: '🟢', fusionado: '🔵', derivado: '🟣', muerto: '🔴', reserva: '🟡', en_paso: '⚪', cliente: '🔵', olt: '🔵',
}
const ESTADO_LABEL: Record<EstadoFibra, string> = {
  libre: 'Libre', fusionado: 'Fusionada', derivado: 'A splitter', muerto: 'Muerta/cortada', reserva: 'Reserva',
  en_paso: 'En paso', cliente: 'A cliente', olt: 'A OLT',
}

function extremoLibre(): EstadoExtremoFibra {
  return { estado: 'libre', conectadoA: null, splitterConectado: null }
}

type TipoCable = 'TRONCAL' | 'DISTRIBUCION' | 'ACOMETIDA' | 'DROP'

interface Fibra {
  numero: number
  color: string
  fibraId?: string
  /** Estado real, por extremo del cable -- fuente de verdad. */
  extremos: { origen: EstadoExtremoFibra; destino: EstadoExtremoFibra }
  notas?: string
}

interface BufferTubo {
  numero: number
  color: string
  fibras: Fibra[]
}

interface CableConfig {
  tipo: TipoCable
  numeroFibras: number
  tieneBuffers: boolean
  buffers: BufferTubo[]
  fibras: Fibra[] // Para cables sin buffer (drop, acometida)
  fabricante?: string
  modelo?: string
  diametro?: number
  pesoKm?: number
  longitudTotal?: number
  /** Gazas/reservas: metros de cable de sobra enrollados cerca de cada punta. */
  reservaOrigenMetros?: number
  reservaDestinoMetros?: number
}

interface CableConfigFormProps {
  config?: CableConfig
  onChange: (config: CableConfig) => void
  /** Nombres simples de los nodos en cada punta (ej. "EA-RC-001"), para que
   *  el usuario identifique el extremo sin tener que leer un UUID. */
  origenNombre?: string
  destinoNombre?: string
}

const CONFIGURACIONES_CABLE = [
  { fibras: 2, buffers: 0, label: '2 FO (Drop)' },
  { fibras: 6, buffers: 0, label: '6 FO (Sin buffer)' },
  { fibras: 12, buffers: 1, label: '12 FO (1 buffer)' },
  { fibras: 24, buffers: 2, label: '24 FO (2 buffers)' },
  { fibras: 48, buffers: 4, label: '48 FO (4 buffers)' },
  { fibras: 72, buffers: 6, label: '72 FO (6 buffers)' },
  { fibras: 96, buffers: 8, label: '96 FO (8 buffers)' },
  { fibras: 144, buffers: 12, label: '144 FO (12 buffers)' },
  { fibras: 288, buffers: 24, label: '288 FO (24 buffers)' },
]

/** Todas las fibras existentes (buffer o sueltas) en un orden estable, para
 *  poder preservar su estado al cambiar la cantidad de fibras/buffers. */
function todasLasFibras(config: Pick<CableConfig, 'tieneBuffers' | 'buffers' | 'fibras'>): Fibra[] {
  if (config.tieneBuffers) return config.buffers.flatMap(b => b.fibras)
  return config.fibras
}

function fibraTieneUso(f: Fibra): boolean {
  return f.extremos.origen.estado !== 'libre' || f.extremos.destino.estado !== 'libre'
}

export function CableConfigForm({ config, onChange, origenNombre, destinoNombre }: CableConfigFormProps) {
  const [localConfig, setLocalConfig] = useState<CableConfig>(config || {
    tipo: 'DISTRIBUCION',
    numeroFibras: 12,
    tieneBuffers: true,
    buffers: [],
    fibras: [],
  })

  const [expandedBuffer, setExpandedBuffer] = useState<number | null>(null)

  // Generar estructura de fibras, reutilizando el extremos/fibraId de las que
  // ya existían (por posición) en vez de reiniciarlas todas a 'libre'.
  const generarEstructura = (numeroFibras: number, tieneBuffers: boolean, existentes: Fibra[]) => {
    const paraPosicion = (i: number): Fibra => {
      const previa = existentes[i]
      return previa || { numero: 0, color: '', extremos: { origen: extremoLibre(), destino: extremoLibre() } }
    }

    if (!tieneBuffers || numeroFibras <= 12) {
      const fibras: Fibra[] = Array.from({ length: numeroFibras }, (_, i) => {
        const previa = paraPosicion(i)
        return { ...previa, numero: i + 1, color: previa.color || COLORES_FIBRA[i % 12].codigo }
      })
      return { buffers: [], fibras }
    } else {
      const numBuffers = Math.ceil(numeroFibras / 12)
      let idx = 0
      const buffers: BufferTubo[] = Array.from({ length: numBuffers }, (_, bufferIdx) => {
        const fibrasEnBuffer = bufferIdx === numBuffers - 1 ? (numeroFibras % 12 || 12) : 12
        const fibras = Array.from({ length: fibrasEnBuffer }, (_, fibraIdx) => {
          const previa = paraPosicion(idx)
          idx++
          return { ...previa, numero: fibraIdx + 1, color: previa.color || COLORES_FIBRA[fibraIdx].codigo }
        })
        return { numero: bufferIdx + 1, color: COLORES_FIBRA[bufferIdx % 12].codigo, fibras }
      })
      return { buffers, fibras: [] }
    }
  }

  useEffect(() => {
    if (!config) {
      const estructura = generarEstructura(12, true, [])
      setLocalConfig(prev => ({ ...prev, ...estructura }))
    }
  }, [])

  const updateConfig = (updates: Partial<CableConfig>) => {
    const newConfig = { ...localConfig, ...updates }
    setLocalConfig(newConfig)
    onChange(newConfig)
  }

  const aplicarConfiguracion = (fibras: number, buffers: number) => {
    const tieneBuffers = buffers > 0
    const existentes = todasLasFibras(localConfig)
    if (fibras < existentes.length) {
      const sePerderian = existentes.slice(fibras).filter(fibraTieneUso)
      if (
        sePerderian.length > 0 &&
        !confirm(
          `${sePerderian.length} fibra(s) que ya están en uso (fusionadas, a splitter, etc.) van a quedar fuera de la nueva configuración. ¿Continuar de todas formas?`
        )
      ) {
        return
      }
    }
    const estructura = generarEstructura(fibras, tieneBuffers, existentes)
    updateConfig({
      numeroFibras: fibras,
      tieneBuffers,
      ...estructura
    })
  }

  /** Corrección manual de UN extremo de una fibra -- no toca el otro extremo. */
  const actualizarExtremo = (bufferNum: number | null, fibraNum: number, extremo: 'origen' | 'destino', estado: EstadoFibra) => {
    const aplicar = (f: Fibra): Fibra =>
      f.numero === fibraNum
        ? { ...f, extremos: { ...f.extremos, [extremo]: { estado, conectadoA: null, splitterConectado: null } } }
        : f

    if (bufferNum !== null) {
      updateConfig({ buffers: localConfig.buffers.map(b => (b.numero === bufferNum ? { ...b, fibras: b.fibras.map(aplicar) } : b)) })
    } else {
      updateConfig({ fibras: localConfig.fibras.map(aplicar) })
    }
  }

  const getColorHex = (colorCodigo: string) => {
    return COLORES_FIBRA.find(c => c.codigo === colorCodigo)?.hex || '#CCCCCC'
  }

  const totalFibrasUsadas = todasLasFibras(localConfig).filter(fibraTieneUso).length
  const origenLabel = origenNombre || 'Origen'
  const destinoLabel = destinoNombre || 'Destino'

  const renderExtremoSelect = (fibra: Fibra, bufferNum: number | null, extremo: 'origen' | 'destino', label: string) => (
    <div>
      <div className="text-[9px] text-gray-500 truncate" title={label}>{label}</div>
      <select
        value={fibra.extremos[extremo].estado}
        onChange={(e) => actualizarExtremo(bufferNum, fibra.numero, extremo, e.target.value as EstadoFibra)}
        className="w-full text-[10px] bg-transparent border-0 p-0 text-center"
      >
        {ESTADOS_MANUALES.map((estado) => (
          <option key={estado} value={estado}>{ESTADO_ICONO[estado]} {ESTADO_LABEL[estado]}</option>
        ))}
        {!ESTADOS_MANUALES.includes(fibra.extremos[extremo].estado) && (
          <option value={fibra.extremos[extremo].estado}>
            {ESTADO_ICONO[fibra.extremos[extremo].estado]} {ESTADO_LABEL[fibra.extremos[extremo].estado]}
          </option>
        )}
      </select>
    </div>
  )

  const renderFibraCard = (fibra: Fibra, bufferNum: number | null) => (
    <div
      key={fibra.numero}
      className="p-2 border rounded text-center text-xs bg-white dark:bg-dark-700"
      style={{ borderColor: getColorHex(fibra.color) }}
    >
      <Circle className="w-3 h-3 mx-auto mb-1" fill={getColorHex(fibra.color)} stroke={getColorHex(fibra.color)} />
      <div className="font-medium">F{fibra.numero}</div>
      <div className="grid grid-cols-2 gap-1 mt-1">
        {renderExtremoSelect(fibra, bufferNum, 'origen', origenLabel)}
        {renderExtremoSelect(fibra, bufferNum, 'destino', destinoLabel)}
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Tipo y configuración básica */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Tipo de Cable</label>
          <select
            value={localConfig.tipo}
            onChange={(e) => updateConfig({ tipo: e.target.value as TipoCable })}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            <option value="TRONCAL">Troncal (Feeder)</option>
            <option value="DISTRIBUCION">Distribución</option>
            <option value="ACOMETIDA">Acometida</option>
            <option value="DROP">Drop</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Configuración</label>
          <select
            value={`${localConfig.numeroFibras}-${localConfig.buffers.length}`}
            onChange={(e) => {
              const [fibras, buffers] = e.target.value.split('-').map(Number)
              aplicarConfiguracion(fibras, buffers)
            }}
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          >
            {CONFIGURACIONES_CABLE.map(cfg => (
              <option key={cfg.label} value={`${cfg.fibras}-${cfg.buffers}`}>
                {cfg.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Fabricante</label>
          <input
            type="text"
            value={localConfig.fabricante || ''}
            onChange={(e) => updateConfig({ fabricante: e.target.value })}
            placeholder="Ej: Furukawa, Prysmian"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Modelo</label>
          <input
            type="text"
            value={localConfig.modelo || ''}
            onChange={(e) => updateConfig({ modelo: e.target.value })}
            placeholder="Ej: CFOA-SM-AS80"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
        </div>
      </div>

      {/* Gazas / reservas de cable */}
      <div>
        <h4 className="font-medium flex items-center gap-2 mb-2">
          <Ruler className="w-4 h-4" />
          Gazas / Reservas de Cable
        </h4>
        <p className="text-xs text-gray-500 mb-2">
          Metros de cable de sobra enrollados cerca de cada punta (para poder reabrir el
          empalme sin tender cable nuevo). La distancia lineal en el mapa siempre va a
          parecer menor que la longitud real instalada -- esto es lo que falta para
          completarla.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Reserva en {origenLabel} (m)</label>
            <input
              type="number"
              min={0}
              step="0.5"
              value={localConfig.reservaOrigenMetros ?? ''}
              onChange={(e) => updateConfig({ reservaOrigenMetros: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })}
              placeholder="0"
              className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Reserva en {destinoLabel} (m)</label>
            <input
              type="number"
              min={0}
              step="0.5"
              value={localConfig.reservaDestinoMetros ?? ''}
              onChange={(e) => updateConfig({ reservaDestinoMetros: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })}
              placeholder="0"
              className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
            />
          </div>
        </div>
        {typeof localConfig.longitudTotal === 'number' && (
          <div className="mt-2 text-sm bg-gray-50 dark:bg-dark-700 rounded-lg p-2 flex justify-between">
            <span className="text-gray-500">Distancia lineal (mapa): {localConfig.longitudTotal.toFixed(1)} m</span>
            <span className="font-medium text-orange-600">Longitud real (con gazas): {longitudRealCable(localConfig).toFixed(1)} m</span>
          </div>
        )}
      </div>

      {/* Estructura de fibras */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Estructura del Cable ({localConfig.numeroFibras} fibras)
          </h4>
          <div className="text-sm text-gray-500">
            {totalFibrasUsadas} en uso / {localConfig.numeroFibras} total
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Cada fibra tiene dos puntas independientes: <strong>{origenLabel}</strong> y <strong>{destinoLabel}</strong>.
          Empalmarla o asignarla a un splitter en un extremo no ocupa el otro.
        </p>

        {localConfig.tieneBuffers && localConfig.buffers.length > 0 ? (
          // Vista con buffers
          <div className="space-y-2">
            {localConfig.buffers.map((buffer) => (
              <div key={buffer.numero} className="border rounded-lg dark:border-dark-600 overflow-hidden">
                {/* Header del buffer */}
                <button
                  type="button"
                  onClick={() => setExpandedBuffer(expandedBuffer === buffer.numero ? null : buffer.numero)}
                  className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-700"
                  style={{ borderLeft: `4px solid ${getColorHex(buffer.color)}` }}
                >
                  <div className="flex items-center gap-3">
                    <Circle
                      className="w-4 h-4"
                      fill={getColorHex(buffer.color)}
                      stroke={getColorHex(buffer.color)}
                    />
                    <span className="font-medium">Buffer {buffer.numero} - {buffer.color.charAt(0).toUpperCase() + buffer.color.slice(1)}</span>
                    <span className="text-sm text-gray-500">({buffer.fibras.length} fibras)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-green-600">{buffer.fibras.filter(f => !fibraTieneUso(f)).length} libres</span>
                    <span className="text-blue-600">{buffer.fibras.filter(fibraTieneUso).length} en uso</span>
                    <span>{expandedBuffer === buffer.numero ? '▼' : '▶'}</span>
                  </div>
                </button>

                {/* Fibras del buffer */}
                {expandedBuffer === buffer.numero && (
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 border-t dark:border-dark-600">
                    <div className="grid grid-cols-4 gap-2">
                      {buffer.fibras.map((fibra) => renderFibraCard(fibra, buffer.numero))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Vista sin buffers (fibras sueltas)
          <div className="border rounded-lg p-4 dark:border-dark-600">
            <div className="grid grid-cols-4 gap-2">
              {localConfig.fibras.map((fibra) => renderFibraCard(fibra, null))}
            </div>
          </div>
        )}
      </div>

      {/* Leyenda de colores */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
        <h4 className="font-medium mb-3">Código de Colores (TIA-598)</h4>
        <div className="grid grid-cols-6 gap-2">
          {COLORES_FIBRA.map((color, index) => (
            <div key={color.codigo} className="flex items-center gap-2 text-xs">
              <Circle 
                className="w-3 h-3 flex-shrink-0" 
                fill={color.hex} 
                stroke={color.hex === '#FFFFFF' ? '#999' : color.hex}
              />
              <span>{index + 1}. {color.nombre}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
